import { useAuth0 } from '@auth0/auth0-react'
import { isPlatform } from '@ionic/react'

import axios from 'axios'
import { BackButton } from 'components/BackButton'
import dayjs from 'dayjs'
import { ReportStatusTypes } from 'models/ReportStatus.model'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import { AuthPromptModal, AuthPromptModalMode } from '../components/AuthPromptModal'
import { ErrorView } from '../components/Error'
import { Footer } from '../components/Footer'
import { EditableManageField, heroInputClass } from '../components/pageStatus/EditableManageField'
import {
  HospitalEditDraft,
  hospitalToEditDraft,
  isValidSupportEmail,
  sanitizePhoneInput,
} from '../components/pageStatus/HospitalEditFields'
import { ManagePageBar } from '../components/pageStatus/ManagePageBar'
import { HOSPITAL_PLACEHOLDER_IMAGE, OrganizationLogo } from '../components/pageStatus/OrganizationLogo'
import { PageStatusSection, useMyPageEntry } from '../components/pageStatus/PageStatusSection'
import { PendingReviewBanner } from '../components/pageStatus/PendingReviewBanner'
import { VerifiedPageCard } from '../components/pageStatus/VerifiedPageCard'
import { ProgressBar } from '../components/ProgressBar'
import { Spinner } from '../components/Spinner'
import { StarRating } from '../components/StarRating'
import { TopLogo } from '../components/TopLogo'
import { useAppToast } from '../hooks/useAppToast'
import { shouldShowVerifiedBadge, usePageViewerRole } from '../hooks/usePageViewerRole'
import { FormSubmissionSummaryDTO } from '../models/FormSubmissionSummaryDTO.model'
import { Hospital } from '../models/Hospital.model'
import { USStates } from '../models/USStates.model'
import { useAuthToken } from '../providers/AuthStateContext'
import { HospitalService } from '../services/HospitalService/HospitalService'
import {
  getPendingReportIdsFromReviews,
  isReviewReportPending,
  mergeReportedReviewIds,
  ReviewService,
} from '../services/ReportReview/ReportReviewService'
import UserService from '../services/UserService/UserService'
import { navigateBackToDirectory } from '../utils/directorySearchUrl'
import { canPerformVerifiedAction } from '../utils/emailVerification'
import { formatRating } from '../utils/formatRating'
import { applyScrollToReviewFromLocationState, DetailPageLocationState } from '../utils/reviewSuccessNavigation'

//-----------------------------------------------------------------------------
// SUB COMPONENTS
//-----------------------------------------------------------------------------

type HospitalSummaryItemProps = {
  summary: {
    name: string
    average: number
  }
}
const HospitalSummaryItem = ({ summary }: HospitalSummaryItemProps) => {
  const formatCategoryName = (name: string): string => {
    return name
      .replace(/\//g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }
  const hasData = summary.average > 0
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-base font-medium text-white">{formatCategoryName(summary.name)}</span>
        <span className="text-base font-medium text-white">
          {hasData ? `${formatRating(summary.average)} / 5` : 'N/A'}
        </span>
      </div>
      <ProgressBar value={summary.average} max={5} />
    </div>
  )
}

type HospitalReviewItemProps = {
  review: FormSubmissionSummaryDTO
  onReport: (reviewId: number) => void
  isReported: boolean
  isAuthenticated: boolean
}
const HospitalReviewItem = ({ review, onReport, isReported, isAuthenticated }: HospitalReviewItemProps) => {
  return (
    <li
      id={`review-${review.id}`}
      className="mb-6 bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex flex-col">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-500 mb-1">
              {dayjs(review.createdAt).format('MMMM D, YYYY')}
            </div>
            <div className="text-base font-semibold text-graphite-grey mb-2">
              {review.position && <span dangerouslySetInnerHTML={{ __html: review.position }}></span>}
              {review.position && review.title && review.title !== 'null' && (
                <span className="mx-2 text-gray-400">•</span>
              )}
              {review.title && review.title !== 'null' && (
                <span dangerouslySetInnerHTML={{ __html: review.title }}></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StarRating rating={review.averageRating} size="md" />
            <span className="text-base font-medium text-gray-600">{review.averageRating.toFixed(1)}</span>
          </div>
        </div>

        {review.additionalFeedback && (
          <div className="flex flex-col gap-3">
            <div className="text-base text-gray-700 leading-relaxed text-justify">
              <p dangerouslySetInnerHTML={{ __html: review.additionalFeedback }}></p>
            </div>
            {isAuthenticated && (
              <div className="flex justify-end">
                {isReported ? (
                  <span
                    className="text-sm text-gray-400 opacity-50 cursor-not-allowed"
                    aria-label="Review Already Reported"
                    title="Review Already Reported"
                  >
                    Reported
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onReport(review.id)}
                    className="text-sm text-gray-500 hover:text-gray-600 transition-colors"
                    aria-label="Report Review"
                    title="Report Review"
                  >
                    Report
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

//-----------------------------------------------------------------------------
// MAIN COMPONENT
//-----------------------------------------------------------------------------

const HospitalDetail = (): JSX.Element => {
  const { user, isAuthenticated } = useAuth0()
  const { token, isTokenReady } = useAuthToken()
  const { permalink } = useParams<{ permalink?: string }>()
  const [hospital, setHospital] = useState<Hospital>()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>()
  const { showErrorToast, showSuccessToast } = useAppToast()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [reviewIdToReport, setReviewIdToReport] = useState<number | null>(null)
  const [reportedReviewIds, setReportedReviewIds] = useState<Set<number>>(new Set())
  const [authPromptMode, setAuthPromptMode] = useState<AuthPromptModalMode | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [activeField, setActiveField] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<HospitalEditDraft | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [claimRefreshKey, setClaimRefreshKey] = useState(0)
  const history = useHistory()
  const location = useLocation()

  const formattedUser = user ? UserService.formatUser(user) : undefined
  const isOrgAdmin = !!formattedUser?.permissions?.includes('organization_admin')
  const myPageEntry = useMyPageEntry(hospital?.id ?? 0, 'hospital', isAuthenticated, isTokenReady, claimRefreshKey)

  const viewerRole = usePageViewerRole({
    isOrgAdmin,
    myPageEntry: myPageEntry ?? null,
    pageClaimStatus: hospital?.claimStatus,
    pageId: hospital?.id ?? 0,
    pageType: 'hospital',
  })
  const showVerifiedBadge = shouldShowVerifiedBadge(viewerRole, 'hospital')

  const hospitalService = new HospitalService()

  const getOgImageUrl = (hospitalId: number): string => {
    const baseApiUrl = process.env.REACT_APP_API_URL || 'https://c6jmzjja1l.execute-api.us-east-1.amazonaws.com/prod'
    // Remove trailing slash if present, then append the OG image path
    const cleanBaseUrl = baseApiUrl.replace(/\/$/, '')
    return `${cleanBaseUrl}/og-image/hospital/${hospitalId}`
  }

  const fetchHospitalData = async (options?: { preserveEditing?: boolean }) => {
    let searchOneOptions: {
      id?: number
      permalink?: string
    } = {
      permalink: permalink || '',
    }

    if (permalink && !isNaN(permalink as unknown as number)) {
      searchOneOptions = {
        id: parseInt(permalink),
      }
    }

    setIsLoading(true)

    return hospitalService
      .searchOne(searchOneOptions)
      .then((resultHospital) => {
        setIsLoading(false)
        const hospitalData = {
          ...resultHospital,
          image: resultHospital.image || HOSPITAL_PLACEHOLDER_IMAGE,
        }
        setHospital(hospitalData)
        setEditDraft(hospitalToEditDraft(hospitalData))
        if (!options?.preserveEditing) {
          setIsEditing(false)
        }
        const pendingFromReviews = getPendingReportIdsFromReviews(hospitalData.reviews ?? [])
        if (pendingFromReviews.length > 0) {
          setReportedReviewIds((prev) => mergeReportedReviewIds(prev, pendingFromReviews))
        }
      })
      .catch((err: Error & { status?: number }) => {
        console.error(err)
        if (err.status === 404) {
          history.push('/404')
        } else {
          setError(err.message)
          setIsLoading(false)
        }
      })
  }

  useEffect(() => {
    if (!isTokenReady) return
    fetchHospitalData()
  }, [permalink, isTokenReady, token])

  useEffect(() => {
    const scrollToReviewId = (location.state as DetailPageLocationState | undefined)?.scrollToReviewId
    if (!scrollToReviewId || isLoading || !hospital) {
      return
    }

    const timer = window.setTimeout(() => {
      applyScrollToReviewFromLocationState(scrollToReviewId, hospital.reviews)
      history.replace({ pathname: location.pathname, search: location.search, state: {} })
    }, 100)

    return () => window.clearTimeout(timer)
  }, [history, hospital, isLoading, location.pathname, location.search, location.state])

  useEffect(() => {
    if (!isAuthenticated || !isTokenReady || !token) return

    let cancelled = false

    const fetchReportedReviews = async () => {
      try {
        const reviewService = new ReviewService()
        const ids = await reviewService.getMyReportedReviews()
        if (!cancelled) {
          setReportedReviewIds((prev) => mergeReportedReviewIds(prev, ids))
        }
      } catch (err) {
        console.error('Failed to fetch reported reviews:', err)
      }
    }

    fetchReportedReviews()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, isTokenReady, token])

  const handleReport = async (reviewId: number) => {
    if (!isAuthenticated && !user) {
      window.location.href = `/login/redirect/${encodeURIComponent(window.location.pathname)}`
      return
    }

    try {
      const reviewService = new ReviewService()
      await reviewService.reportReview(reviewId, ReportStatusTypes.ACTIVE)

      setReportedReviewIds((prev) => new Set(prev).add(reviewId))

      showSuccessToast('Review reported successfully!')
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status
        const msg = error.response?.data?.message || error.message

        if (status === 401) {
          window.location.href = `/login/redirect/${encodeURIComponent(window.location.pathname)}`
          return
        }

        showErrorToast(`Failed to report review: ${msg}`)
        return
      }

      if (error instanceof Error) {
        showErrorToast(`Failed to report review: ${error.message}`)
      } else {
        showErrorToast('Failed to report review: Unknown error')
      }
    }
  }

  const handleReportClick = (reviewId: number) => {
    if (!isAuthenticated) {
      window.location.href = `/login/redirect/${encodeURIComponent(location.pathname)}`
      return
    }
    if (!canPerformVerifiedAction(isAuthenticated, user)) {
      setAuthPromptMode('verify-email')
      return
    }
    setReviewIdToReport(reviewId)
    setShowConfirmModal(true)
  }

  const handleConfirmReport = () => {
    if (reviewIdToReport !== null) {
      setShowConfirmModal(false)
      handleReport(reviewIdToReport)
      setReviewIdToReport(null)
    }
  }

  const handleCancelReport = () => {
    setShowConfirmModal(false)
    setReviewIdToReport(null)
  }

  const handleShare = async () => {
    if (!hospital) return

    const reviewCount = hospital.reviewCount || 0
    const reviewText = reviewCount > 1 ? 's' : ''
    const rating = Number(hospital.rating || 0)

    const isMobileDevice = isPlatform('android') || isPlatform('ios')
    const webBaseUrl = process.env.REACT_APP_WEB_URL || 'https://scrubstr.com'
    const shareUrl = isMobileDevice ? `${webBaseUrl}/hospitals/${hospital.permalink}` : window.location.href

    const shareData = {
      text: `Check out reviews for ${hospital.name} on ScrubStr - ${rating.toFixed(1)} stars based on ${reviewCount} review${reviewText}`,
      title: `${hospital.name} - Hospital Reviews`,
      url: shareUrl,
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData)
        showSuccessToast('Shared successfully!')
      } else {
        await navigator.clipboard.writeText(shareUrl)
        showSuccessToast('Link copied to clipboard!')
      }
    } catch (error) {
      console.error('Error sharing:', error)
    }
  }

  const handleClaimStatusChange = async () => {
    setClaimRefreshKey((k) => k + 1)
    await fetchHospitalData()

    showSuccessToast('Claim request submitted successfully! Our team will review it shortly.')
  }

  const handleManage = () => {
    if (hospital) setEditDraft(hospitalToEditDraft(hospital))
    setActiveField(null)
    setEmailError('')
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    if (hospital) setEditDraft(hospitalToEditDraft(hospital))
    setActiveField(null)
    setEmailError('')
    setIsEditing(false)
  }

  const handleLogoSelect = async (file: File) => {
    if (!hospital) return
    setIsUploadingLogo(true)
    try {
      await hospitalService.uploadLogo(hospital.id, file)
      await fetchHospitalData({ preserveEditing: true })
    } catch (err) {
      console.error(err)
      showErrorToast('Failed to upload logo.')
    } finally {
      setIsUploadingLogo(false)
    }
  }

  const handleProposeChanges = async () => {
    if (!hospital || !editDraft) return

    if (!isValidSupportEmail(editDraft.supportEmail)) {
      setEmailError('Please enter a valid email address.')
      setActiveField('email')
      return
    }

    setEmailError('')
    setIsSaving(true)
    try {
      await hospitalService.update(hospital.id, {
        address: editDraft.address || null,
        city: editDraft.city || null,
        name: editDraft.name,
        state: editDraft.state || undefined,
        supportEmail: editDraft.supportEmail || null,
        supportTelephone: editDraft.supportTelephone || null,
        website: editDraft.website || null,
        zipcode: editDraft.zip || null,
      })
      await fetchHospitalData()
      setActiveField(null)
      setEmailError('')
      setIsEditing(false)
      showSuccessToast('Changes submitted for review.')
    } catch (err) {
      console.error(err)
      showErrorToast('Failed to submit changes.')
    } finally {
      setIsSaving(false)
    }
  }

  const displayData = useMemo(() => {
    if (!hospital) return null
    if (isEditing && editDraft) return editDraft
    return hospitalToEditDraft(hospital)
  }, [hospital, isEditing, editDraft])

  const deactivateField = () => setActiveField(null)
  const logoInputRef = React.useRef<HTMLInputElement>(null)

  const updateDraft = (patch: Partial<HospitalEditDraft>) => {
    setEditDraft((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  const formatDraftLocation = (draft: HospitalEditDraft): string => {
    return [draft.city, draft.state].filter(Boolean).join(', ')
  }

  const formatDraftAddress = (draft: HospitalEditDraft): string | null => {
    const cityState = formatDraftLocation(draft)
    if (draft.address) {
      return cityState ? `${draft.address}, ${cityState}` : draft.address
    }
    return cityState || null
  }

  useEffect(() => {
    if (showConfirmModal) {
      document.body.style.overflow = 'hidden'
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'hidden'
      }
    } else {
      document.body.style.overflow = ''
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'auto'
      }
    }
    return () => {
      document.body.style.overflow = ''
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'auto'
      }
    }
  }, [showConfirmModal])

  return (
    <div id="page-root" className="h-screen overflow-auto relative">
      <TopLogo />

      <div className="mt-6 px-4 max-w-xl lg:max-w-6xl mx-auto relative">
        <div className="mb-4">
          <BackButton onClick={() => navigateBackToDirectory(history, location)} />
        </div>
        {error && <ErrorView>{error}</ErrorView>}
        {isLoading && !error && (
          <div className="flex justify-center items-center py-20 min-h-[400px]">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && !error && hospital && (
          <>
            <Helmet>
              <title>{hospital.name} - ScrubStr</title>
              <meta property="og:title" content={`${hospital.name} - ScrubStr`} />
              <meta
                property="og:description"
                content={`${hospital.name}${hospital.city && hospital.state.code ? ` - ${hospital.city}, ${hospital.state.code}` : ''}${Number(hospital.rating || 0) > 0 && hospital.reviewCount ? ` - ${Number(hospital.rating || 0).toFixed(1)} stars based on ${hospital.reviewCount} review${hospital.reviewCount > 1 ? 's' : ''}` : ''} on ScrubStr`}
              />
              <meta property="og:url" content={window.location.href} />
              <meta property="og:type" content="website" />
              <meta property="og:image" content={getOgImageUrl(hospital.id)} />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={`${hospital.name} - ScrubStr`} />
              <meta
                name="twitter:description"
                content={`${hospital.name}${hospital.city && hospital.state.code ? ` - ${hospital.city}, ${hospital.state.code}` : ''}${Number(hospital.rating || 0) > 0 && hospital.reviewCount ? ` - ${Number(hospital.rating || 0).toFixed(1)} stars based on ${hospital.reviewCount} review${hospital.reviewCount > 1 ? 's' : ''}` : ''} on ScrubStr`}
              />
              <meta name="twitter:image" content={getOgImageUrl(hospital.id)} />
            </Helmet>

            {viewerRole === 'owner' && (
              <div className="mb-4">
                <ManagePageBar
                  isEditing={isEditing}
                  isSaving={isSaving}
                  onManage={handleManage}
                  onProposeChanges={handleProposeChanges}
                  onCancel={handleCancelEdit}
                />
              </div>
            )}

            {hospital.customizationStatus === 'pending' && (
              <PendingReviewBanner className="mb-4">
                <p className="text-xs leading-relaxed text-gray-600">
                  <span className="font-semibold text-graphite-grey">Your changes are under review.</span> We&apos;ll
                  notify you when they&apos;re approved.
                </p>
              </PendingReviewBanner>
            )}

            <div className="mb-6 rounded-2xl p-6 bg-aqua-mint">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {displayData && (
                    <>
                      <EditableManageField
                        fieldId="name"
                        isManaging={isEditing}
                        activeField={activeField}
                        onEdit={setActiveField}
                        onDeactivate={deactivateField}
                        className="mb-1"
                        renderEditor={() => (
                          <input
                            className={`${heroInputClass} text-2xl font-bold`}
                            value={displayData.name}
                            onChange={(e) => updateDraft({ name: e.target.value })}
                          />
                        )}
                      >
                        <h1 className="text-2xl font-bold text-white">
                          {displayData.name}
                          {showVerifiedBadge && (
                            <span className="ml-1.5 inline-flex align-middle">
                              <VerifiedPageCard />
                            </span>
                          )}
                        </h1>
                      </EditableManageField>

                      {(formatDraftLocation(displayData) || isEditing) && (
                        <EditableManageField
                          fieldId="location"
                          isManaging={isEditing}
                          activeField={activeField}
                          onEdit={setActiveField}
                          onDeactivate={deactivateField}
                          className="mt-1"
                          renderEditor={() => (
                            <div className="flex flex-wrap gap-2">
                              <input
                                className={`${heroInputClass} min-w-[8rem] flex-1`}
                                placeholder="City"
                                value={displayData.city}
                                onChange={(e) => updateDraft({ city: e.target.value })}
                              />
                              <select
                                className={`${heroInputClass} w-24`}
                                value={displayData.state}
                                onChange={(e) => updateDraft({ state: e.target.value as USStates })}
                              >
                                <option value="" className="text-gray-900">
                                  State
                                </option>
                                {Object.values(USStates).map((code) => (
                                  <option key={code} value={code} className="text-gray-900">
                                    {code}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        >
                          {formatDraftLocation(displayData) ? (
                            <p className="text-base text-white/90">{formatDraftLocation(displayData)}</p>
                          ) : (
                            <p className="text-base italic text-white/60">Add location</p>
                          )}
                        </EditableManageField>
                      )}
                    </>
                  )}

                  {Number(hospital.rating || 0) > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <StarRating rating={Number(hospital.rating)} size="md" />
                      <span className="text-base font-semibold text-white">{Number(hospital.rating).toFixed(1)}</span>
                      <span className="text-sm text-white/90">
                        {hospital.reviewCount} review{hospital.reviewCount && hospital.reviewCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to={`/review-hospital/${hospital.id}`}
                      className="inline-block rounded-xl bg-orange px-4 py-2 text-base font-semibold text-white transition-all hover:bg-opacity-90"
                    >
                      Write Review
                    </Link>
                    <button
                      type="button"
                      onClick={handleShare}
                      className="inline-flex items-center gap-2 rounded-xl border border-white px-4 py-2 text-base font-semibold text-white transition-all hover:bg-white/10"
                    >
                      Share
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>
                  </div>

                  {displayData && (formatDraftAddress(displayData) || isEditing) && (
                    <EditableManageField
                      fieldId="address"
                      isManaging={isEditing}
                      activeField={activeField}
                      onEdit={setActiveField}
                      onDeactivate={deactivateField}
                      className="mt-4"
                      renderEditor={() => (
                        <div className="space-y-2">
                          <input
                            className={heroInputClass}
                            placeholder="Street address"
                            value={displayData.address}
                            onChange={(e) => updateDraft({ address: e.target.value })}
                          />
                          <input
                            className={`${heroInputClass} w-32`}
                            placeholder="ZIP"
                            value={displayData.zip}
                            onChange={(e) => updateDraft({ zip: e.target.value })}
                          />
                        </div>
                      )}
                    >
                      <span className="inline-flex items-start gap-2 text-sm text-white/90">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{formatDraftAddress(displayData) || 'Add address'}</span>
                      </span>
                    </EditableManageField>
                  )}

                  {displayData && isEditing && (
                    <div className="mt-4 space-y-3 border-t border-white/20 pt-4">
                      {(displayData.website || isEditing) && (
                        <EditableManageField
                          fieldId="website"
                          isManaging={isEditing}
                          activeField={activeField}
                          onEdit={setActiveField}
                          onDeactivate={deactivateField}
                          renderEditor={() => (
                            <input
                              className={heroInputClass}
                              placeholder="https://example.com"
                              type="url"
                              inputMode="url"
                              autoComplete="url"
                              value={displayData.website}
                              onChange={(e) => updateDraft({ website: e.target.value })}
                            />
                          )}
                        >
                          {displayData.website ? (
                            <a
                              href={
                                displayData.website.startsWith('http')
                                  ? displayData.website
                                  : `https://${displayData.website}`
                              }
                              className="text-sm text-sky-200 hover:underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {displayData.website}
                            </a>
                          ) : (
                            <span className="text-sm italic text-white/60">Add website</span>
                          )}
                        </EditableManageField>
                      )}
                      {(displayData.supportTelephone || isEditing) && (
                        <EditableManageField
                          fieldId="phone"
                          isManaging={isEditing}
                          activeField={activeField}
                          onEdit={setActiveField}
                          onDeactivate={deactivateField}
                          renderEditor={() => (
                            <input
                              className={heroInputClass}
                              placeholder="Phone number"
                              type="tel"
                              inputMode="tel"
                              autoComplete="tel"
                              value={displayData.supportTelephone}
                              onChange={(e) => updateDraft({ supportTelephone: sanitizePhoneInput(e.target.value) })}
                            />
                          )}
                        >
                          {displayData.supportTelephone ? (
                            <span className="text-sm text-white/90">{displayData.supportTelephone}</span>
                          ) : (
                            <span className="text-sm italic text-white/60">Add phone</span>
                          )}
                        </EditableManageField>
                      )}
                      {(displayData.supportEmail || isEditing) && (
                        <EditableManageField
                          fieldId="email"
                          isManaging={isEditing}
                          activeField={activeField}
                          onEdit={setActiveField}
                          onDeactivate={deactivateField}
                          renderEditor={() => (
                            <div>
                              <input
                                className={heroInputClass}
                                placeholder="Email address"
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                value={displayData.supportEmail}
                                onChange={(e) => {
                                  const value = e.target.value
                                  updateDraft({ supportEmail: value })
                                  if (emailError && isValidSupportEmail(value)) {
                                    setEmailError('')
                                  }
                                }}
                              />
                              {emailError && <p className="mt-1 text-sm text-red-300">{emailError}</p>}
                            </div>
                          )}
                        >
                          <div>
                            {displayData.supportEmail ? (
                              <span className="text-sm text-white/90">{displayData.supportEmail}</span>
                            ) : (
                              <span className="text-sm italic text-white/60">Add email</span>
                            )}
                            {emailError && activeField !== 'email' && (
                              <p className="mt-1 text-sm text-red-300">{emailError}</p>
                            )}
                          </div>
                        </EditableManageField>
                      )}
                    </div>
                  )}
                </div>

                <div className="relative shrink-0">
                  <OrganizationLogo imageUrl={displayData?.image ?? hospital.image} alt={hospital.name} />
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                      className="absolute bottom-1 right-1 rounded-full bg-white/90 p-1.5 shadow hover:bg-white disabled:opacity-60"
                      aria-label="Change logo"
                    >
                      <svg className="h-4 w-4 text-graphite-grey" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) void handleLogoSelect(file)
                      e.target.value = ''
                    }}
                  />
                </div>
              </div>
            </div>

            <PageStatusSection
              role={viewerRole}
              organizationType="hospital"
              organizationId={hospital.id}
              organizationName={hospital.name}
              claimStatus={hospital.claimStatus}
              myPageEntry={myPageEntry ?? null}
              userEmail={user?.email}
              onClaimStatusChange={handleClaimStatusChange}
            />

            {/* Rating Breakdown Section */}
            {hospital.ratingsSummary && hospital.ratingsSummary.length > 0 && (
              <div className="mb-8 rounded-lg p-6 bg-aqua-mint">
                <h3 className="text-xl font-bold text-white mb-6">Rating Breakdown</h3>
                <div className="space-y-4">
                  {hospital.ratingsSummary.map((x, i) => (
                    <HospitalSummaryItem summary={x} key={i} />
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(hospital.reviews) && hospital.reviews.length > 0 && (
              <div className="mb-8 bg-gray-50 rounded-lg p-6">
                <h3 id="detail-page-reviews" className="text-2xl font-bold text-graphite-grey mb-6" tabIndex={-1}>
                  Reviews Received
                </h3>
                <ul className="space-y-4">
                  {hospital.reviews.map((x) => (
                    <HospitalReviewItem
                      review={x}
                      key={x.id}
                      onReport={handleReportClick}
                      isReported={isReviewReportPending(x.id, x.reportStatus, reportedReviewIds)}
                      isAuthenticated={isAuthenticated}
                    />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-12">
        <Footer />
      </div>

      {/* Confirmation Modal */}
      {authPromptMode && (
        <AuthPromptModal returnTo={location.pathname} mode={authPromptMode} onClose={() => setAuthPromptMode(null)} />
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={handleCancelReport} />
          <div className="relative z-[10001] bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">Confirm Report</h2>
            <p className="text-base text-center text-gray-700 mb-6">Are you sure you want to report this review?</p>
            <div className="flex gap-3 w-full">
              <button
                onClick={handleCancelReport}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReport}
                className="bg-orange flex-1 px-4 py-2 text-white rounded-md font-medium hover:bg-red-700 transition-colors"
              >
                Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HospitalDetail
